/**
 * AdminMessenger.jsx - Secțiune messenger pentru administrator
 * Afișează toate mesajele de la angajați și permite răspuns
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MessageCircle, Send, AlertTriangle, HelpCircle, Info, 
  Check, CheckCheck, Archive, Trash2, RefreshCw, Search,
  User, Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const messageTypeConfig = {
  error: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Problemă' },
  request: { icon: HelpCircle, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Solicitare' },
  general: { icon: Info, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', label: 'General' }
};

export default function AdminMessenger({ companyId, adminId, adminName }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    fetchMessages();
  }, [companyId]);
  
  const fetchMessages = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages/admin/${companyId}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Eroare la încărcarea mesajelor");
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };
  
  const handleReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/messages/${selectedMessage.id}/reply`, {
        message_id: selectedMessage.id,
        reply_text: replyText,
        replier_id: adminId,
        replier_name: adminName || 'Administrator'
      });
      
      toast.success("Răspuns trimis!");
      setReplyText('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error("Eroare la trimiterea răspunsului");
      console.error("Error sending reply:", error);
    } finally {
      setSending(false);
    }
  };
  
  const handleArchive = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/archive`);
      toast.success("Mesaj arhivat");
      fetchMessages();
    } catch (error) {
      toast.error("Eroare la arhivare");
    }
  };
  
  const handleDelete = async (messageId) => {
    if (!confirm("Sigur vrei să ștergi acest mesaj?")) return;
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success("Mesaj șters");
      fetchMessages();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}z`;
    return date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' });
  };
  
  const filteredMessages = messages.filter(msg => {
    const matchesSearch = !searchTerm || 
      msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'unread' && msg.status === 'unread') ||
      (activeTab === 'error' && msg.message_type === 'error') ||
      (activeTab === 'request' && msg.message_type === 'request');
    
    return matchesSearch && matchesTab;
  });
  
  const unreadCount = messages.filter(m => m.status === 'unread').length;
  const errorCount = messages.filter(m => m.message_type === 'error' && m.status !== 'replied').length;
  
  return (
    <Card className="border-indigo-200" data-testid="admin-messenger">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-indigo-600" />
              Mesaje de la Angajați
              {unreadCount > 0 && (
                <Badge className="bg-red-500 ml-2">{unreadCount} necitite</Badge>
              )}
            </CardTitle>
            <CardDescription>Comunicare directă cu echipa ta</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizează
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Caută mesaje..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="all" className="text-xs">
              Toate ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="unread" className="text-xs">
              Necitite ({unreadCount})
            </TabsTrigger>
            <TabsTrigger value="error" className="text-xs">
              Probleme ({errorCount})
            </TabsTrigger>
            <TabsTrigger value="request" className="text-xs">
              Solicitări
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Messages List */}
        <ScrollArea className="h-[500px] pr-1">
          <div className="space-y-3 pr-3">
          {loading ? (
            <p className="text-center text-slate-500 py-8">Se încarcă...</p>
          ) : filteredMessages.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nu există mesaje</p>
          ) : (
            filteredMessages.map(msg => {
              const config = messageTypeConfig[msg.message_type] || messageTypeConfig.general;
              const TypeIcon = config.icon;
              
              return (
                <Card 
                  key={msg.id} 
                  className={`${config.bg} ${config.border} ${msg.status === 'unread' ? 'ring-2 ring-indigo-300' : ''}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className={`w-4 h-4 ${config.color} flex-shrink-0`} />
                          <span className="font-semibold text-sm truncate">{msg.subject}</span>
                          {msg.status === 'unread' && (
                            <Badge className="bg-yellow-500 text-xs flex-shrink-0">Nou</Badge>
                          )}
                          {msg.status === 'replied' && (
                            <CheckCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                          <User className="w-3 h-3" />
                          <span className="font-medium">{msg.sender_name}</span>
                          <Clock className="w-3 h-3 ml-2" />
                          <span>{formatDate(msg.created_at)}</span>
                        </div>
                        
                        <p className="text-sm text-slate-700 line-clamp-2">{msg.message}</p>
                        
                        {/* Show replies */}
                        {msg.replies && msg.replies.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 border-green-400">
                            <p className="text-xs text-green-700 font-semibold">
                              Ai răspuns ({msg.replies.length})
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button 
                          size="sm" 
                          variant="default"
                          className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                          onClick={() => {
                            setSelectedMessage(msg);
                            if (msg.status === 'unread') handleMarkAsRead(msg.id);
                          }}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Răspunde
                        </Button>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleArchive(msg.id)}
                            title="Arhivează"
                          >
                            <Archive className="w-3 h-3 text-slate-500" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleDelete(msg.id)}
                            title="Șterge"
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
          </div>
        </ScrollArea>
      </CardContent>
      
      {/* Reply Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Răspunde la mesaj
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              {/* Original Message */}
              <Card className="bg-slate-50">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="font-semibold text-sm">{selectedMessage.sender_name}</span>
                  </div>
                  <p className="font-medium text-sm mb-1">{selectedMessage.subject}</p>
                  <p className="text-sm text-slate-600">{selectedMessage.message}</p>
                </CardContent>
              </Card>
              
              {/* Previous Replies */}
              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500">Răspunsuri anterioare:</p>
                  {selectedMessage.replies.map(reply => (
                    <div key={reply.id} className="pl-3 border-l-2 border-green-400 py-1">
                      <p className="text-sm">{reply.text}</p>
                      <p className="text-xs text-slate-400">
                        {reply.replier_name} - {formatDate(reply.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Input */}
              <div>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrie răspunsul tău..."
                  rows={3}
                  className="resize-none"
                />
              </div>
              
              <Button 
                onClick={handleReply}
                disabled={sending || !replyText.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {sending ? "Se trimite..." : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Trimite Răspunsul
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
